import React from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import {
  HomeIcon,
  UserGroupIcon,
  UserIcon,
  CogIcon,
  ChartBarIcon,
  CalendarIcon,
  DocumentTextIcon,
  BookOpenIcon
} from '@heroicons/react/24/outline';

interface RoleBasedNavProps {
  onNavigate?: () => void;
}

const RoleBasedNav: React.FC<RoleBasedNavProps> = ({ onNavigate }) => {
  const { user, activeRole } = useAuthStore();

  if (!user || !activeRole) return null;

  const isConsultant = activeRole === 'CONSULTANT';
  const isTrainer = activeRole === 'TRAINER';
  const isClient = activeRole === 'CLIENT';

  const navItems = [
    {
      name: 'Dashboard',
      href: '/dashboard',
      icon: HomeIcon,
      show: true // Everyone sees dashboard
    },
    {
      name: 'My Profile',
      href: '/profile',
      icon: UserIcon,
      show: true // Everyone sees their profile
    },
    {
      name: 'My Workouts',
      href: '/client/workouts',
      icon: CalendarIcon,
      show: isClient // Only clients see their own workouts
    },
    {
      name: 'My Meals',
      href: '/client/meals',
      icon: DocumentTextIcon,
      show: isClient // Only clients see their own meals
    },
    {
      name: 'My Progress',
      href: '/client/progress',
      icon: ChartBarIcon,
      show: isClient // Only clients see their own progress
    },
    {
      name: 'My Clients',
      href: '/clients',
      icon: UserGroupIcon,
      show: isTrainer
    },
    {
      name: 'Libraries',
      href: '/libraries',
      icon: BookOpenIcon,
      show: isTrainer || isConsultant
    },
    {
      name: 'User Management',
      href: '/admin/users',
      icon: CogIcon,
      show: isConsultant
    }
  ];

  const visibleItems = navItems.filter(item => item.show);

  return (
    <nav className="space-y-1">
      {visibleItems.map((item) => {
        const Icon = item.icon;
        return (
          <Link
            key={item.name}
            to={item.href}
            onClick={onNavigate}
            className="group flex items-center px-2 py-2 text-sm font-medium rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-50"
          >
            <Icon
              className="mr-3 flex-shrink-0 h-6 w-6 text-gray-400 group-hover:text-gray-500"
              aria-hidden="true"
            />
            {item.name}
          </Link>
        );
      })}
    </nav>
  );
};

export default RoleBasedNav;
